import React, { useEffect, useRef, useState } from "react";
import { X, Music, Send, Heart, DollarSign, Sparkles, Phone, PhoneOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useCallerInPeer from "../lib/useCallerInPeer";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * RadioListenerActions — viewer-facing engagement controls for a live show.
 *
 * Renders two side-by-side buttons that float over the station player:
 *
 *   ♫ Request   → opens a modal where listeners type a song title/artist.
 *                 Capped at 3 pending per session (rate-limited backend-side).
 *
 *   ♥ Tip       → opens a modal with one-tap $1/$5/$10 buckets that debit
 *                 the wallet and surface as a floating heart in the chat
 *                 (heart animation handled by the host studio listener).
 *
 * Only renders when `liveSession` is truthy — i.e. a host is actually
 * on-air. Without that, song requests have no destination and tips are
 * better routed through the regular creator-tip flow.
 */
export default function RadioListenerActions({ stationSlug, liveSession, className = "" }) {
  const [open, setOpen] = useState(null); // 'request' | 'tip' | 'call' | null
  // Lightweight call status — surfaces "📞 You're #3" pill once user joins.
  const [callStatus, setCallStatus] = useState(null);

  // Poll the listener's queue position every 6s once they've joined the
  // queue. Stops polling after they leave or get put on-air.
  useEffect(() => {
    if (!stationSlug || !liveSession) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/radio/station/${stationSlug}/call/me`);
        if (!alive) return;
        setCallStatus(data?.in_queue ? data : null);
      } catch { /* ignore — keep last status */ }
    };
    tick();
    const id = setInterval(tick, 6000);
    return () => { alive = false; clearInterval(id); };
  }, [stationSlug, liveSession]);

  if (!liveSession) return null;

  return (
    <>
      <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="radio-listener-actions">
        <button
          data-testid="radio-action-request"
          onClick={() => setOpen("request")}
          className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border border-white/15"
        >
          <Music className="w-3.5 h-3.5" /> Request a song
        </button>
        <button
          data-testid="radio-action-tip"
          onClick={() => setOpen("tip")}
          className="bg-tv-accent hover:bg-yellow-400 text-black text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
        >
          <Heart className="w-3.5 h-3.5" /> Tip the host
        </button>
        {/* Call-in: prominently labelled "Call In" when not in queue,
            switches to a position pill once the listener has joined. */}
        {callStatus && callStatus.in_queue ? (
          <button
            data-testid="radio-action-call-status"
            onClick={() => setOpen("call")}
            className={`text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${
              callStatus.status === "onair"
                ? "bg-red-600 text-white animate-pulse"
                : callStatus.status === "private"
                ? "bg-amber-500 text-black"
                : "bg-emerald-500 text-black"
            }`}
          >
            <Phone className="w-3.5 h-3.5" />
            {callStatus.status === "onair"
              ? "You're ON AIR"
              : callStatus.status === "private"
              ? "Host whispering"
              : `You're #${callStatus.position}`}
          </button>
        ) : (
          <button
            data-testid="radio-action-call"
            onClick={() => setOpen("call")}
            className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
          >
            <Phone className="w-3.5 h-3.5" /> Call in
          </button>
        )}
      </div>

      {open === "request" && (
        <RequestSongModal
          stationSlug={stationSlug}
          hostName={liveSession.host_name}
          onClose={() => setOpen(null)}
        />
      )}
      {open === "tip" && (
        <TipHostModal
          hostId={liveSession.host_id}
          hostName={liveSession.host_name}
          stationSlug={stationSlug}
          sessionId={liveSession.id}
          onClose={() => setOpen(null)}
        />
      )}
      {open === "call" && (
        <CallInModal
          stationSlug={stationSlug}
          liveSession={liveSession}
          callStatus={callStatus}
          onClose={() => setOpen(null)}
          onChange={(s) => setCallStatus(s)}
        />
      )}
    </>
  );
}

function RequestSongModal({ stationSlug, hostName, onClose }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return toast.error("Song title required");
    setBusy(true);
    try {
      await api.post(`/radio/station/${stationSlug}/request`, {
        title: title.trim(),
        artist: artist.trim(),
        note: note.trim(),
      });
      toast.success(`Sent to ${hostName || "the host"} — they'll see it in their queue`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title="Request a song" onClose={onClose} testid="radio-request-modal">
      <input
        autoFocus
        data-testid="radio-request-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Song title"
        className="w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
      />
      <input
        data-testid="radio-request-artist"
        value={artist}
        onChange={(e) => setArtist(e.target.value)}
        placeholder="Artist (optional)"
        className="w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
      />
      <textarea
        data-testid="radio-request-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional shoutout (max 200 chars)"
        maxLength={200}
        rows={2}
        className="w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-4 py-3 text-white text-sm placeholder-white/30"
      />
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 text-white/80 text-sm hover:bg-white/10">Cancel</button>
        <button
          data-testid="radio-request-submit"
          onClick={submit}
          disabled={busy || !title.trim()}
          className="px-5 py-2 rounded-lg bg-tv-accent text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
        >
          <Send className="w-4 h-4" /> Send request
        </button>
      </div>
    </ModalShell>
  );
}

const TIP_BUCKETS = [1, 5, 10, 20];

function TipHostModal({ hostId, hostName, stationSlug, sessionId, onClose }) {
  const [amount, setAmount] = useState(5);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    api.get("/funds/balance").then((r) => setBalance(r.data?.balance_usd ?? null)).catch(() => {});
  }, []);

  const tip = async () => {
    if (amount <= 0) return;
    setBusy(true);
    try {
      await api.post("/funds/tip", {
        creator_id: hostId,
        amount,
        message: message.trim(),
        radio_session_id: sessionId,
        radio_station_slug: stationSlug,
      });
      toast.success(`Sent $${amount} to ${hostName || "the host"} — they'll see it on-air`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const insufficient = balance != null && balance < amount;

  return (
    <ModalShell title={`Tip ${hostName || "the host"}`} onClose={onClose} testid="radio-tip-modal">
      <div className="grid grid-cols-4 gap-2">
        {TIP_BUCKETS.map((v) => (
          <button
            key={v}
            data-testid={`radio-tip-amount-${v}`}
            onClick={() => setAmount(v)}
            className={`py-3 rounded-lg font-display font-black text-lg transition ${
              amount === v ? "bg-tv-accent text-black" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
            }`}
          >${v}</button>
        ))}
      </div>
      <div className="text-xs text-white/50 text-center" data-testid="radio-tip-balance">
        Wallet: {balance == null ? "…" : `$${balance.toFixed(2)}`}
        {insufficient && (
          <a href="/funds" className="ml-2 text-tv-accent font-bold underline">Add funds →</a>
        )}
      </div>
      <textarea
        data-testid="radio-tip-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Optional message (e.g. shoutout, thank you, request)"
        maxLength={280}
        rows={2}
        className="w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-4 py-3 text-white text-sm placeholder-white/30"
      />
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 text-white/80 text-sm hover:bg-white/10">Cancel</button>
        <button
          data-testid="radio-tip-submit"
          onClick={tip}
          disabled={busy || amount <= 0 || insufficient}
          className="px-5 py-2 rounded-lg bg-tv-accent text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
        >
          <DollarSign className="w-4 h-4" /> Send ${amount}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, testid, children }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-gradient-to-r from-fuchsia-500/10 via-rose-500/10 to-amber-500/10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-tv-accent" />
            <div className="font-display font-bold text-white text-sm">{title}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid={`${testid}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
      </div>
    </div>
  );
}

/**
 * CallInModal — listener side of caller-in.
 *
 * Three distinct states:
 *   1. NOT IN QUEUE  → mic permission test → "Join queue" button
 *   2. WAITING       → "You're #N" + leave button + skip-the-queue $5 tip
 *   3. ON AIR        → big "You're on the air" header, mic active, leave button
 *
 * Mic-permission test:
 *   We request `getUserMedia({ audio })` BEFORE adding the user to the
 *   queue so a listener with no mic / blocked permission learns that
 *   immediately, not after they've waited 10 minutes for the host. The
 *   stream is held in a ref so when the host promotes them we can attach
 *   it to the existing WebRTC peer pipeline (Phase 2 — see component
 *   docstring).
 */
function CallInModal({ stationSlug, liveSession, callStatus, onClose, onChange }) {
  const [topic, setTopic] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [micOk, setMicOk] = useState(null); // null=untested, true/false
  const [micTesting, setMicTesting] = useState(false);
  const streamRef = useRef(null);
  // Force the useCallerInPeer hook to re-attach the SAME stream by stable
  // reference. We pin it in a state variable so the hook's dep-array
  // re-runs when (and only when) the stream actually changes.
  const [pinnedStream, setPinnedStream] = useState(null);
  const hostAudioRef = useRef(null);
  const { token } = useAuth();

  const inQueue = !!callStatus?.in_queue;
  const onAir = callStatus?.status === "onair";
  const isPrivate = callStatus?.status === "private";
  const peerActive = onAir || isPrivate;

  // ── WebRTC voice bridge — caller side ──
  // Active any time the host has paired with us (onair OR private). We
  // always send mic; we receive host audio back through the same peer
  // when the host enables private-mode talk-back. Host's voice plays
  // through a hidden <audio> element so the caller hears them clearly.
  useCallerInPeer({
    enabled: peerActive && !!pinnedStream,
    role: "caller",
    stationSlug,
    token: token || "",
    localStream: pinnedStream,
    apiBase: API_BASE,
    onConnected: () => toast.success("Mic connected to host"),
    onRemoteStream: (s) => {
      if (hostAudioRef.current) {
        hostAudioRef.current.srcObject = s;
        hostAudioRef.current.play().catch(() => {});
      }
    },
    onClosed: () => { /* host hung up — UI already reflects via callStatus poll */ },
  });

  // Tear down mic stream when modal closes or component unmounts.
  useEffect(() => {
    return () => {
      try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch {}
      streamRef.current = null;
    };
  }, []);

  const testMic = async () => {
    setMicTesting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
      streamRef.current = stream;
      setPinnedStream(stream);
      setMicOk(true);
    } catch {
      setMicOk(false);
      toast.error("Mic blocked. Allow microphone access to call in.");
    } finally {
      setMicTesting(false);
    }
  };

  const join = async () => {
    if (!micOk) return toast.error("Test your mic first");
    setBusy(true);
    try {
      const { data } = await api.post(`/radio/station/${stationSlug}/call/join`, {
        topic: topic.trim(),
        city: city.trim(),
      });
      onChange?.({ in_queue: true, id: data.id, status: data.status, position: undefined });
      toast.success("You're in the queue — keep this tab open");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const leave = async () => {
    setBusy(true);
    try {
      await api.post(`/radio/station/${stationSlug}/call/leave`);
      onChange?.(null);
      try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch {}
      streamRef.current = null;
      toast("Left the queue");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const skipQueue = async () => {
    // 1. Charge a $5 tip via the wallet endpoint.
    setBusy(true);
    try {
      const { data: tip } = await api.post("/funds/tip", {
        creator_id: liveSession.host_id,
        amount: 5,
        message: "Skip the queue",
        radio_session_id: liveSession.id,
        radio_station_slug: stationSlug,
      });
      // 2. Tell the queue endpoint to bump priority.
      await api.post(`/radio/station/${stationSlug}/call/skip-queue`, { tip_id: tip?.tip_id });
      toast.success("Bumped to the front 🚀");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={onAir ? "You're ON AIR" : inQueue ? `You're in the queue` : "Call in to the show"} onClose={onClose} testid="radio-call-modal">
      {!inQueue && (
        <>
          <div className="text-white/70 text-sm">
            Joining the queue lets <strong>{liveSession.host_name || "the host"}</strong> bring you on the air during their show. Test your mic first so we know we can hear you.
          </div>
          <button
            data-testid="radio-call-mic-test"
            onClick={testMic}
            disabled={micTesting || micOk}
            className={`w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 ${
              micOk ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/40"
              : "bg-white/5 text-white/85 border border-white/15 hover:bg-white/10"
            }`}
          >
            {micTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {micOk ? "✓ Mic ready" : micTesting ? "Testing mic…" : "Test microphone"}
          </button>
          <input
            data-testid="radio-call-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What do you want to talk about? (optional)"
            maxLength={200}
            className="w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-4 py-3 text-white text-sm placeholder-white/30"
          />
          <input
            data-testid="radio-call-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Where are you calling from? (optional)"
            maxLength={80}
            className="w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-4 py-3 text-white text-sm placeholder-white/30"
          />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 text-white/80 text-sm hover:bg-white/10">Cancel</button>
            <button
              data-testid="radio-call-join"
              onClick={join}
              disabled={busy || !micOk}
              className="px-5 py-2 rounded-lg bg-emerald-500 text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Phone className="w-4 h-4" /> Join the queue
            </button>
          </div>
        </>
      )}

      {inQueue && !onAir && (
        <>
          <div className="text-center py-4">
            <div className="text-7xl font-display font-black text-emerald-400 mb-1" data-testid="radio-call-position">
              #{callStatus.position ?? "?"}
            </div>
            <div className="text-white/60 text-sm">in the queue</div>
            <div className="text-white/40 text-xs mt-1">Keep this tab open — we'll buzz you the moment you're up.</div>
          </div>
          <button
            data-testid="radio-call-skip"
            onClick={skipQueue}
            disabled={busy}
            className="w-full py-3 rounded-lg bg-tv-accent hover:bg-yellow-400 text-black font-black text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <DollarSign className="w-4 h-4" /> Skip the queue · $5 tip
          </button>
          <button
            data-testid="radio-call-leave"
            onClick={leave}
            disabled={busy}
            className="w-full py-2 rounded-lg bg-white/5 hover:bg-rose-500/20 text-white/70 hover:text-rose-200 text-sm flex items-center justify-center gap-2"
          >
            <PhoneOff className="w-4 h-4" /> Leave the queue
          </button>
        </>
      )}

      {onAir && (
        <>
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full font-black uppercase tracking-wider animate-pulse">
              <Phone className="w-4 h-4" /> ON AIR
            </div>
            <div className="text-white/70 text-sm mt-3">{liveSession.host_name || "The host"} can hear you. Speak up.</div>
          </div>
          <button
            data-testid="radio-call-leave-onair"
            onClick={leave}
            disabled={busy}
            className="w-full py-2 rounded-lg bg-rose-500/20 text-rose-200 text-sm flex items-center justify-center gap-2"
          >
            <PhoneOff className="w-4 h-4" /> Hang up
          </button>
        </>
      )}

      {isPrivate && (
        <>
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-2 bg-amber-500/90 text-black px-4 py-2 rounded-full font-black uppercase tracking-wider">
              <Phone className="w-4 h-4" /> PRIVATE
            </div>
            <div className="text-white/70 text-sm mt-3">{liveSession.host_name || "The host"} is talking to you privately. Listeners can't hear this.</div>
            <div className="text-white/40 text-xs mt-1">Stay on — you may be put on the air shortly.</div>
          </div>
          <button
            data-testid="radio-call-leave-private"
            onClick={leave}
            disabled={busy}
            className="w-full py-2 rounded-lg bg-rose-500/20 text-rose-200 text-sm flex items-center justify-center gap-2"
          >
            <PhoneOff className="w-4 h-4" /> Hang up
          </button>
        </>
      )}

      {/* Hidden audio sink — plays the host's voice during private mode. */}
      <audio ref={hostAudioRef} autoPlay data-testid="radio-call-host-audio" style={{ display: "none" }} />
    </ModalShell>
  );
}
